import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserCheck, FileText, FileCheck2, ReceiptIndianRupee,
  Truck, Boxes, BarChart3, UserCog, Settings, LogOut, User, X, ShieldCheck, Wallet, GraduationCap,
  Wrench, Cpu, PiggyBank,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";

const MAIN = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/app/crm", label: "CRM", icon: Users, module: "crm" },
  { to: "/app/customers", label: "Customers", icon: UserCheck, module: "customers" },
  { to: "/app/quotations", label: "Quotations", icon: FileText, module: "quotations" },
  { to: "/app/proformas", label: "Proforma Invoices", icon: FileCheck2, module: "proformas" },
  { to: "/app/invoices", label: "Invoices", icon: ReceiptIndianRupee, module: "invoices" },
  { to: "/app/receivables", label: "Receivables", icon: Wallet, module: "invoices" },
  { to: "/app/purchase-orders", label: "Purchase Orders", icon: Truck, module: "purchase_orders" },
  { to: "/app/inventory", label: "Inventory", icon: Boxes, module: "inventory" },
  { to: "/app/demos", label: "Demos & Training", icon: GraduationCap, module: "demos" },
  { to: "/app/repairs", label: "Repair Estimates", icon: Wrench, module: "repairs" },
  { to: "/app/processing", label: "Data Processing", icon: Cpu, module: "processing" },
  { to: "/app/profitability", label: "Profitability", icon: PiggyBank, module: "profitability" },
];

const ADMIN = [
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/staff", label: "Staff Management", icon: UserCog },
  { to: "/app/settings", label: "Admin Settings", icon: Settings },
];

const Item = ({ to, label, icon: Icon, onNavigate }) => (
  <NavLink
    to={to}
    onClick={onNavigate}
    data-testid={`nav-${to.split("/").pop()}`}
    className={({ isActive }) =>
      cn(
        "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
        "transition-colors duration-200",
        isActive
          ? "bg-sidebar-hover text-primary-foreground"
          : "text-sidebar-foreground/75 hover:bg-sidebar-hover hover:text-sidebar-foreground"
      )
    }
  >
    {({ isActive }) => (
      <>
        <span
          className={cn(
            "absolute left-0 top-1/2 h-0 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-active transition-all duration-300",
            isActive && "h-6"
          )}
        />
        <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive && "text-sidebar-active")} />
        <span className="truncate">{label}</span>
      </>
    )}
  </NavLink>
);

export const Sidebar = ({ onNavigate, onClose }) => {
  const { user, can, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const visible = MAIN.filter((m) => can(m.module, "view"));

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside className="grain flex h-full w-[260px] flex-col bg-gradient-rail">
      <div className="flex items-center gap-3 border-b border-sidebar-border px-5 py-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-warm text-primary-foreground shadow-glow">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-bold text-sidebar-foreground">Megatronix Vision</p>
          <p className="truncate text-[11px] text-sidebar-muted">ERP &amp; CRM</p>
        </div>
        {onClose && (
          <Button variant="ghost" size="iconSm" onClick={onClose} className="text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground lg:hidden">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <nav className="scroll-slim flex-1 overflow-y-auto px-3 py-4">
        <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted">Workspace</p>
        <div className="space-y-1">
          {visible.map((m) => <Item key={m.to} {...m} onNavigate={onNavigate} />)}
        </div>

        {isAdmin && (
          <>
            <p className="px-3 pb-2 pt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted">Administration</p>
            <div className="space-y-1">
              {ADMIN.map((m) => <Item key={m.to} {...m} onNavigate={onNavigate} />)}
            </div>
          </>
        )}

        <p className="px-3 pb-2 pt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted">Account</p>
        <div className="space-y-1">
          <Item to="/app/profile" label="My Profile" icon={User} onNavigate={onNavigate} />
          <button
            onClick={doLogout}
            data-testid="sidebar-logout"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/75 transition-colors hover:bg-destructive/15 hover:text-destructive"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Logout
          </button>
        </div>
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <NavLink
          to="/app/profile"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-sidebar-hover"
        >
          <UserAvatar name={user?.name} src={user?.avatar} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-sidebar-foreground">{user?.name}</p>
            <p className="truncate text-[11px] text-sidebar-muted">{user?.email}</p>
          </div>
        </NavLink>
      </div>
    </aside>
  );
};
